import { Controller } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/high-res.css';
import { cn } from '@skupreme/shared/ui';
import type { ControlledPhoneFormFieldProps } from 'types/common/form-fields';
import {
  CustomField,
  FieldLabel,
  FieldMessage,
  InputContainer,
} from './chrome';
import { FIELD_INPUT, FIELD_INPUT_INVALID } from './styles';

/*
 * react-phone-input-2's own sheet styles `.react-tel-input .form-control` at
 * (0,2,0), so the kit look needs `!` here as well as against Semantic.
 */
const PHONE_CONTAINER = 'sk:relative sk:w-full! sk:font-sans!';
const PHONE_INPUT = cn(FIELD_INPUT, 'sk:w-full! sk:pl-12!');
const PHONE_BUTTON = cn(
  'sk:top-0! sk:bottom-0! sk:left-0! sk:rounded-l-full! sk:border-0! sk:bg-transparent!',
  'sk:[&_.selected-flag]:rounded-l-full! sk:[&_.selected-flag]:bg-transparent! sk:[&_.selected-flag]:pl-3!',
);
const PHONE_DROPDOWN = cn(
  'sk:z-3000! sk:my-1! sk:w-72! sk:rounded-card! sk:bg-surface! sk:py-1!',
  'sk:inset-shadow-hairline sk:shadow-card!',
  'sk:[&_.country]:text-dense! sk:[&_.country]:text-fg!',
  'sk:[&_.country.highlight]:bg-fill-secondary! sk:[&_.country:hover]:bg-fill-tertiary!',
  'sk:[&_.search]:bg-surface!',
);

export function ControlledPhoneFormField({
  control,
  name,
  defaultValue = '',
  required,
  error,
  inline,
  inputRight,
  label,
  hint,
  width,
  fieldClass,
  inputContainerClass,
  onChange,
  helpText,
  helpPosition,
  setDialCodeLength,
  country: _country,
  countryCode,
  enforceHeightCompression: _enforceHeightCompression,
  ...rest
}: ControlledPhoneFormFieldProps) {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange: controlOnChange, value } }) => (
        <CustomField
          width={width}
          inline={inline || false}
          className={fieldClass}
        >
          <FieldLabel
            label={label}
            required={required}
            helpText={helpText}
            helpPosition={helpPosition}
          />
          <InputContainer inline={inline} className={inputContainerClass}>
            <PhoneInput
              country={countryCode || 'us'}
              value={value ?? ''}
              onChange={(phone, data) => {
                controlOnChange(phone);
                if (setDialCodeLength && 'dialCode' in data) {
                  setDialCodeLength(data.dialCode.length);
                }
                if (onChange) {
                  onChange(phone);
                }
              }}
              enableSearch
              disableSearchIcon
              specialLabel=""
              containerClass={PHONE_CONTAINER}
              inputClass={cn(PHONE_INPUT, !!error && FIELD_INPUT_INVALID)}
              buttonClass={PHONE_BUTTON}
              dropdownClass={PHONE_DROPDOWN}
              inputProps={{
                'data-field-value': name,
                'aria-invalid': !!error || undefined,
              }}
              {...rest}
            />
            {inputRight}
          </InputContainer>
          {hint && <FieldMessage>{hint}</FieldMessage>}
        </CustomField>
      )}
    />
  );
}

/*
 * The compressed variant was a few pixels shorter; both are the kit's one
 * input height now, so it is the same field under its old name.
 */
export const ControlledPhoneCompressedFormField = ControlledPhoneFormField;
